import { createEntityAdapter } from '@reduxjs/toolkit';

import { WorkScheduleCalendar } from '@vk-hr-tek/core/calendar/gen/calendar';
import { AppError } from '@vk-hr-tek/core/error';

import {
  Link,
  QuickActionCreateEvent,
  Widget,
  CalendarWidgetCompany,
  PersonalLink,
  GetUserWidgetExternalResponse,
  WidgetPartitionView,
  GetUserWidgetBannerResponse,
  GetWidgetNewsResponse,
  GetWidgetSurveysResponse,
  QuickActionLink,
  AbsenceEmployeeItem,
  WidgetTopNewsItem,
  PostsFeedNewsData,
  FeedPostData,
  WidgetPartitionGrid,
  GetUserWidgetBirthdaysResponse,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';
import { ManageableCompanyItem } from '@app/gen/users';

export interface External extends GetUserWidgetExternalResponse {
  status: 'idle' | 'loading' | 'complete' | 'failed';
}

interface Banner extends GetUserWidgetBannerResponse {
  status: 'idle' | 'loading' | 'complete' | 'failed';
}

// Значения по умолчанию для размеров сетки
const DEFAULT_PARTITIONS_COLS = 2;
const DEFAULT_PARTITIONS_ROWS = 5;

export interface DashboardState {
  status: 'idle' | 'loading' | 'complete' | 'failed';
  error: AppError | null;
  layout: Widget[];
  absences: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    isManager: boolean;
    items: AbsenceEmployeeItem[];
  };
  quickActions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    companyActions: QuickActionCreateEvent[];
    userActions: QuickActionLink[];
    error: AppError | null;
  };
  electronicServices: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: Link[];
  };
  banner: Record<string, Banner>;
  personalLinks: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: PersonalLink[];
  };
  employeePartitions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetPartitionView[];
    rows: number;
    cols: number;
  };
  companyPartitions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetPartitionView[];
    cols: WidgetPartitionGrid['cols'];
    rows: WidgetPartitionGrid['rows'];
    manageableCompanies: ManageableCompanyItem[];
  };
  external: Record<string, External>;
  taskList: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: WidgetPartitionView[];
  };
  newsList: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: GetWidgetNewsResponse['news'];
  };
  calendar: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    companies: CalendarWidgetCompany[];
    companyId: string | undefined;
    includedEmployees: string[];
    workSchedule: WorkScheduleCalendar | null;
  };
  widgets: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
  };
  personalLinksSettings: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    name: string | undefined;
    links: PersonalLink[];
  };
  newsSettings: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    name: string | undefined;
    hasFilter: boolean;
    isFilterAvailable: boolean;
  };
  birthdays: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    data: GetUserWidgetBirthdaysResponse | null;
  };
  surveys: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: GetWidgetSurveysResponse['surveys'];
  };
  surveysSettings: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    name: string | undefined;
  };
  quickActionsSettings: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    companyLinks: QuickActionCreateEvent[];
    userLinks: QuickActionLink[];
  };
  topNews: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    items: WidgetTopNewsItem[];
  };
  feedPostsList: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    total: number;
    posts: (PostsFeedNewsData | FeedPostData)[];
    avatars: {
      status: 'idle' | 'loading' | 'complete' | 'failed';
      list: Record<string, string>;
    };
  };
}

export const initialState: DashboardState = {
  status: 'idle',
  error: null,
  layout: [],
  absences: {
    isManager: false,
    items: [],
    status: 'idle',
  },
  quickActions: {
    status: 'idle',
    companyActions: [],
    userActions: [],
    error: null,
  },
  electronicServices: {
    status: 'idle',
    items: [],
  },
  banner: {},
  personalLinks: {
    status: 'idle',
    items: [],
  },
  employeePartitions: {
    status: 'idle',
    items: [],
    rows: 0,
    cols: 0,
  },
  companyPartitions: {
    status: 'idle',
    items: [],
    cols: DEFAULT_PARTITIONS_COLS,
    rows: DEFAULT_PARTITIONS_ROWS,
    manageableCompanies: [],
  },
  external: {},
  taskList: {
    status: 'idle',
    items: [],
  },
  newsList: {
    status: 'idle',
    items: [],
  },
  calendar: {
    status: 'idle',
    companies: [],
    companyId: undefined,
    includedEmployees: [],
    workSchedule: null,
  },
  widgets: {
    status: 'idle',
    error: null,
  },
  personalLinksSettings: {
    status: 'idle',
    error: null,
    name: undefined,
    links: [],
  },
  newsSettings: {
    status: 'idle',
    error: null,
    name: '',
    hasFilter: false,
    isFilterAvailable: false,
  },
  birthdays: {
    status: 'idle',
    data: null,
  },
  surveys: {
    status: 'idle',
    items: [],
  },
  surveysSettings: {
    status: 'idle',
    error: null,
    name: undefined,
  },
  quickActionsSettings: {
    status: 'idle',
    error: null,
    companyLinks: [],
    userLinks: [],
  },
  topNews: {
    status: 'idle',
    error: null,
    items: [],
  },
  feedPostsList: {
    status: 'idle',
    error: null,
    posts: [],
    total: 0,
    avatars: {
      status: 'idle',
      list: {},
    },
  },
};

export const dashboardAdapter = createEntityAdapter<DynamicEntity>();
